export const WEEKDAY_SHORT = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

export const MONTH_NAMES = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];

export function formatMonthYearLabel(date: Date): string {
  return `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
}

export function parseIso(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return new Date(y ?? 2000, (m ?? 1) - 1, d ?? 1);
}

export function toIso(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function addDays(date: Date, count: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + count);
  return next;
}

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Montag (ISO) der Woche, in der `date` liegt. */
export function startOfWeekMonday(date: Date): Date {
  const copy = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = copy.getDay(); // 0 = So
  const diff = day === 0 ? -6 : 1 - day;
  return addDays(copy, diff);
}

/** ISO-Kalenderwoche (1..53). */
export function isoWeek(date: Date): number {
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayNr = (target.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  const firstDayNr = (firstThursday.getDay() + 6) % 7;
  firstThursday.setDate(firstThursday.getDate() - firstDayNr + 3);
  const diff = target.getTime() - firstThursday.getTime();
  return 1 + Math.round(diff / (7 * 24 * 3600 * 1000));
}

export function enumerateDays(from: Date, to: Date): Date[] {
  const days: Date[] = [];
  let current = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const end = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  while (current <= end) {
    days.push(current);
    current = addDays(current, 1);
  }
  return days;
}

export function isWeekend(date: Date): boolean {
  const day = date.getDay();
  return day === 0 || day === 6;
}

/** ISO-Wochentag: Montag = 1 … Sonntag = 7. */
export function isoWeekday(date: Date): number {
  const day = date.getDay();
  return day === 0 ? 7 : day;
}

const ISO_WEEKDAY_LABELS: Record<number, string> = {
  1: "Montag",
  2: "Dienstag",
  3: "Mittwoch",
  4: "Donnerstag",
  5: "Freitag",
  6: "Samstag",
  7: "Sonntag",
};

/** Kein Arbeitstag laut Zeitmodell (Fallback: Sa/So). */
export function isNonWorkDay(date: Date, nonWorkWeekdays?: number[] | null): boolean {
  const weekdays = nonWorkWeekdays?.length ? nonWorkWeekdays : [6, 7];
  return weekdays.includes(isoWeekday(date));
}

export function nonWorkDayTitle(date: Date, nonWorkWeekdays?: number[] | null): string {
  const label = ISO_WEEKDAY_LABELS[isoWeekday(date)] ?? "Tag";
  if (isWeekend(date) && isNonWorkDay(date, nonWorkWeekdays)) {
    return `Kein Arbeitstag (${label}, Wochenende)`;
  }
  return `Kein Arbeitstag laut Zeitmodell (${label})`;
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function diffDays(from: Date, to: Date): number {
  const a = new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime();
  const b = new Date(to.getFullYear(), to.getMonth(), to.getDate()).getTime();
  return Math.round((b - a) / (24 * 3600 * 1000));
}

export function formatDateDe(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-");
  if (!y || !m || !d) return iso;
  return `${d}.${m}.${y}`;
}

/** Zusammenhängende Segmente gleicher Schlüssel (für Monats-/KW-Kopfzeilen). */
export function groupConsecutive<T>(
  days: Date[],
  keyOf: (d: Date) => string,
  labelOf: (d: Date) => T,
): Array<{ key: string; label: T; span: number; startIdx: number }> {
  const groups: Array<{ key: string; label: T; span: number; startIdx: number }> = [];
  let dayIdx = 0;
  for (const day of days) {
    const key = keyOf(day);
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.span += 1;
    } else {
      groups.push({ key, label: labelOf(day), span: 1, startIdx: dayIdx });
    }
    dayIdx += 1;
  }
  return groups;
}
